import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map, skipWhile, take, tap } from 'rxjs/operators';
import { BehaviorSubject, from, Observable, of, throwError } from 'rxjs';
import { FirebaseService } from './firebase.service';
import * as firebase from 'firebase/app';
import { isNullOrUndefined } from 'util';

@Injectable()
export class AuthService {

  public currentUser: firebase.User = null;

  public status = new BehaviorSubject<firebase.User>(undefined);

  public token = null;

  constructor(private httpClient: HttpClient,
              private firebaseService: FirebaseService) {
    this.listenForSessionChange();
  }

  public login(email: string, password: string): Observable<firebase.auth.UserCredential> {
    return from(this.firebaseService.auth.signInWithEmailAndPassword(email, password));
  }

  public logout(): Observable<void> {
    return from(this.firebaseService.auth.signOut())
      .pipe(
        tap(() => {
          this.currentUser = null;
          this.status.next(null);
        })
      );
  }

  public hasValidSession(): Promise<boolean> {
    // NOTE:
    // When the value is undefined, it means that the system has not yet received any data about the session
    // When the value is null -  we got the session information but there was no session
    // Value should contain the session user from firebase
    return this.status
      .pipe(
        skipWhile((value) => value === undefined),
        take(1),
        map((value) => value === null ? false : true)
      )
      .toPromise();
  }

  private listenForSessionChange(): void {
    this.firebaseService.auth.onAuthStateChanged((user) => {
      if (!user) {
        this.cleanup();
        return;
      }

      user.getIdToken()
        .then((token) => {
          this.token = token;
          this.currentUser = user;
          this.status.next(user);
        })
        .catch(() => {
          console.error('Failed to get token.');
          this.cleanup();
        });
    });
  }

  private cleanup(): void {
    this.token = null;
    this.currentUser = null;
    this.status.next(null);
  }

  public getAuthorizationHeader(): string {
    return isNullOrUndefined(this.token) ? null : 'Bearer ' + this.token;
  }

  public refreshToken(): Observable<string> {
    if (isNullOrUndefined(this.firebaseService.auth.currentUser)) {
      return throwError('Could not refresh token - No active session yet');
    }

    return from(this.firebaseService.auth.currentUser.getIdToken(true))
      .pipe(
        tap(token => {
          // Debug stuff here -
          this.token = token;
        })
      );
  }
}
